// Cognito sign-in is case-sensitive here, and cannot be made otherwise.
//
// The pool was created with `UsernameConfiguration` unset, which is Cognito's
// case-SENSITIVE default, and that setting is immutable — `signInCaseSensitive`
// in CDK would force replacement of the user pool and destroy every account. So
// the only place this can be fixed is before the call goes out. (#748)
//
// Left alone, someone who registers as `Foo@Bar.com` and later types
// `foo@bar.com` is told their credentials are wrong; and if they then create an
// account they get a second, separate one, with its own books, world data and
// subscription, while their real work sits invisibly under the other casing.
// Password managers and mobile keyboards autocapitalise, so that is the likely
// path rather than the exotic one.

/**
 * Lowercase and trim an email so a single person always reaches a single
 * account, whatever their keyboard did.
 *
 * Applied to every Cognito entry point that takes a username. Normalising only
 * sign-up would be worse than not normalising at all: accounts would be created
 * lowercase and then be unreachable by a password reset typed in another case.
 *
 * Non-string input is returned untouched rather than coerced. The Authenticator
 * always supplies a string, and inventing an empty username here would turn a
 * clear "username is required" into an opaque auth failure.
 */
export function normalizeEmail<T>(value: T): T {
  return typeof value === 'string' ? (value.trim().toLowerCase() as unknown as T) : value;
}

/**
 * Normalise the `username` of an Amplify auth input, leaving everything else
 * (password, confirmation code, options) exactly as it was.
 *
 * Sign-up can also carry the address a second time in
 * `options.userAttributes.email`. That copy becomes the stored `email`
 * attribute, so leaving it un-normalised would write `Foo@Bar.com` onto an
 * account reachable only as `foo@bar.com` — the mismatch that makes these
 * accounts hard to reconcile afterwards.
 */
export function normalizeAuthInput<T extends { username?: unknown }>(input: T): T {
  if (!input || typeof input !== 'object') return input;

  const next = { ...input, username: normalizeEmail(input.username) } as T & {
    options?: { userAttributes?: Record<string, unknown> };
  };

  const attrs = next.options?.userAttributes;
  if (attrs && typeof attrs.email === 'string') {
    next.options = {
      ...next.options,
      userAttributes: { ...attrs, email: normalizeEmail(attrs.email) },
    };
  }

  return next;
}
